import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDbtModelsFilters } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import {
  useDatatableContext,
  FilterConfig,
} from "@/components/tables/contexts/DatatableContextProvider";
import AddFiltersButton from "@/components/tables/AddFiltersButton";
import { FilterCondition, FilterType, TableHeaderTypeBase } from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import { DebouncedInput, Stack } from "@altimateai/lego";
import type { ComputedFilters, FilterOptionWithValue } from "./types";
import type { IntegrationFilterOption, IntegrationWithEnvironments } from "@/types/dbtModels";
import { disambiguateProjects } from "./filterUtils";

const GET_DBT_MODELS_FILTERS_NEW = "GET-DBT-MODELS-FILTERS-NEW";

const DROPDOWN_FILTERS: ReadonlyArray<{ uid: string; label: string }> = [
  { uid: "dbt_integrations", label: "Integration" },
  { uid: "environments", label: "Integration Environment" },
  { uid: "resource_types", label: "Resource Type" },
  { uid: "materializations", label: "Materialization" },
  { uid: "projects", label: "Projects" },
  { uid: "databases", label: "Database" },
  { uid: "schemas", label: "Schema" },
];

/**
 * New horizontal filter bar for dbt Models page.
 * Horizontal filter bar for the dbt Models page.
 * Uses the same DataTable filter pattern as NewWarehouseList / Datasets.
 *
 * Cascading behavior (in More Filters panel):
 *   Integration selection → filters Integration Environment options to that integration's environments
 *   Without an integration selection, environment options are empty.
 */
interface DbtModelsNewFiltersProps {
  onSearchChange: (value: string) => void;
}

const DbtModelsNewFilters = ({ onSearchChange }: DbtModelsNewFiltersProps) => {
  const { staleResourcesEnabled } = useAppState();
  const { registerFilters } = useDatatableContext();

  const { data: rawFilters } = useQuery({
    queryKey: [GET_DBT_MODELS_FILTERS_NEW],
    queryFn: async (): Promise<{
      filters: ComputedFilters;
      integrationsWithEnvironments: IntegrationWithEnvironments[];
    }> => {
      const dbtModelsFilters = await getDbtModelsFilters();
      if (!dbtModelsFilters) return { filters: {}, integrationsWithEnvironments: [] };

      // Destructure to exclude integrations_with_environments (not a FilterOption array)
      const { integrations_with_environments, ...dbtModelsFiltersRest } = dbtModelsFilters;
      const filters: ComputedFilters = {
        ...dbtModelsFiltersRest,
        projects: disambiguateProjects(dbtModelsFilters.projects, integrations_with_environments),
        dbt_integrations_combined: dbtModelsFilters.dbt_integrations,
      };

      if (staleResourcesEnabled) {
        filters.is_stale = [
          { label: "Active", value: "false" },
          { label: "Stale", value: "true" },
        ];
      }
      return {
        filters,
        integrationsWithEnvironments: integrations_with_environments,
      };
    },
  });

  const toOptions = (items: (FilterOptionWithValue | IntegrationFilterOption)[] | undefined) =>
    (items || [])
      .filter(item => Boolean(item.label))
      .map(item => ({ label: item.label, value: item.value }));

  const integrationOptions = useMemo(
    () => toOptions(rawFilters?.filters.dbt_integrations_combined),
    [rawFilters?.filters.dbt_integrations_combined]
  );
  const resourceTypeOptions = useMemo(
    () => toOptions(rawFilters?.filters.resource_types),
    [rawFilters?.filters.resource_types]
  );
  const materializationOptions = useMemo(
    () => toOptions(rawFilters?.filters.materializations),
    [rawFilters?.filters.materializations]
  );
  const projectOptions = useMemo(
    () => toOptions(rawFilters?.filters.projects),
    [rawFilters?.filters.projects]
  );
  const databaseOptions = useMemo(
    () => toOptions(rawFilters?.filters.databases),
    [rawFilters?.filters.databases]
  );
  const schemaOptions = useMemo(
    () => toOptions(rawFilters?.filters.schemas),
    [rawFilters?.filters.schemas]
  );
  const isStaleOptions = useMemo(
    () => toOptions(rawFilters?.filters.is_stale),
    [rawFilters?.filters.is_stale]
  );

  // Cascade: environment options depend on selected integrations.
  // When no integration is selected, show all environments across integrations.
  const getEnvironmentOptions = useCallback(
    (activeFilters: FilterCondition[]) => {
      const selectedIntegrations =
        (activeFilters.find(f => f.field === "dbt_integrations")?.value as string[] | undefined) ??
        [];
      const seen = new Set<string>();
      const result: { label: string; value: string }[] = [];
      (rawFilters?.integrationsWithEnvironments ?? [])
        .filter(
          i => !selectedIntegrations.length || selectedIntegrations.includes(i.integration_value)
        )
        .flatMap(i => i.environments)
        .forEach(env => {
          if (env.label && !seen.has(env.label)) {
            seen.add(env.label);
            result.push({ label: env.label, value: env.label });
          }
        });
      return result;
    },
    [rawFilters?.integrationsWithEnvironments]
  );

  const optionsByUid = useMemo<Record<string, { label: string; value: string }[]>>(
    () => ({
      dbt_integrations: integrationOptions,
      resource_types: resourceTypeOptions,
      materializations: materializationOptions,
      projects: projectOptions,
      databases: databaseOptions,
      schemas: schemaOptions,
    }),
    [
      integrationOptions,
      resourceTypeOptions,
      materializationOptions,
      projectOptions,
      databaseOptions,
      schemaOptions,
    ]
  );

  // Register filters keyed by stable searchKey only. Options live in `filterHeaders`
  // (used by the sheet) — keeping them out of registered config avoids re-registering
  // when async option data resolves, which would reset URL-synced filter values to "".
  useEffect(() => {
    const filters: Record<string, FilterConfig> = Object.fromEntries(
      DROPDOWN_FILTERS.map(f => [
        f.uid,
        { label: f.label, filterType: FilterType.DROPDOWN, searchKey: f.uid },
      ])
    );
    if (staleResourcesEnabled) {
      filters.is_stale = {
        label: "Resource Status",
        filterType: FilterType.DROPDOWN,
        searchKey: "is_stale",
      };
    }
    registerFilters(filters);
  }, [staleResourcesEnabled, registerFilters]);

  const noop = () => null;

  const filterHeaders = useMemo((): TableHeaderTypeBase[] => {
    const headers: TableHeaderTypeBase[] = DROPDOWN_FILTERS.map((f, i) => ({
      id: i + 1,
      uid: f.uid,
      label: f.label,
      render: noop,
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: f.uid,
        ...(f.uid === "environments"
          ? { getOptions: getEnvironmentOptions }
          : { options: optionsByUid[f.uid] ?? [] }),
      },
    }));
    if (staleResourcesEnabled) {
      headers.push({
        id: DROPDOWN_FILTERS.length + 1,
        uid: "is_stale",
        label: "Resource Status",
        render: noop,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "is_stale",
          options: isStaleOptions,
        },
      });
    }
    return headers;
  }, [optionsByUid, getEnvironmentOptions, isStaleOptions, staleResourcesEnabled]);

  return (
    <Stack gap="md" align="center" className="al-w-full">
      <DebouncedInput
        debounceMs={1000}
        placeholder="Search dbt models"
        onChange={onSearchChange}
        aria-label="Search dbt models"
        className="al-flex-1"
      />
      <div id="dbt-models-saved-filters-container" />
      <div id="dbt-models-filter-container" />
      <AddFiltersButton
        portals={{
          filter: "dbt-models-filter-container",
          savedFilters: "dbt-models-saved-filters-container",
        }}
        headers={filterHeaders}
        applicableFor={FilterApplicableFor.DBT_MODELS}
      />
    </Stack>
  );
};

export default DbtModelsNewFilters;
